import { RefObject, useCallback, useState } from 'react';
import type { TableComponent } from '@ohpen/enso-ui';

import { isLinkedToActiveTodayOnly, linkedToActiveTodayFilterValue, linkedToActiveTodayKey } from '@pmp/utils';
import type { ListTableChange, ListTableStateProps } from './useListTableState';

export type LinkedToActiveTodaySwitch = {
  linkedToActiveTodayOnly: boolean;
  onToggleLinkedToActiveToday: (checked: boolean) => void;
  onChange: ListTableChange;
  setActiveFilters: (activeFilters: boolean) => void;
};

/**
 * The actions-bar switch that narrows a list to the rows linked to a product active today. The switch owns
 * no filter of its own: it shows whether the `linkedToActiveTodayColumn` filter is exactly Yes, so picking
 * that value in the column header switches it on and anything else off.
 */
export const useLinkedToActiveTodaySwitch = <T>(
  tableRef: RefObject<TableComponent | null>,
  tableState: ListTableStateProps<T>,
): LinkedToActiveTodaySwitch => {
  const [linkedToActiveTodayOnly, setLinkedToActiveTodayOnly] = useState<boolean>(false);

  const onChange = useCallback<ListTableChange>(
    (pagination, filters, ...rest) => {
      tableState.onChange?.(pagination, filters, ...rest);
      setLinkedToActiveTodayOnly(isLinkedToActiveTodayOnly(filters));
    },
    [tableState],
  );

  // Reset Filters and a restored view both write the grid without an AntD change.
  const setActiveFilters = useCallback(
    (activeFilters: boolean) => {
      tableState.setActiveFilters?.(activeFilters);

      const applied = tableRef.current?.getState().filters.byColumn;
      if (applied !== undefined) setLinkedToActiveTodayOnly(isLinkedToActiveTodayOnly(applied));
    },
    [tableState, tableRef],
  );

  const onToggleLinkedToActiveToday = useCallback(
    (checked: boolean) => {
      const table = tableRef.current;
      if (table === null) return;

      const value = linkedToActiveTodayFilterValue(checked);

      table.setFilters({ [linkedToActiveTodayKey]: value });
      // The export reads AntD's filtered snapshot, which only refreshes on an AntD change.
      table.resetFilteredData();
      tableState.onFiltersChange?.({ ...table.getState().filters.byColumn, [linkedToActiveTodayKey]: value });
      setLinkedToActiveTodayOnly(checked);
    },
    [tableState, tableRef],
  );

  return { linkedToActiveTodayOnly, onToggleLinkedToActiveToday, onChange, setActiveFilters };
};
